import {
  ROUTE_PATHS,
  ONBOARDING_ROUTES,
  getDefaultHomePath,
  getOnboardingEntryPath,
  isAddNewBusinessNavigation,
} from '@config/routes';
import usePermission from '@hooks/usePermission';
import PageLoader from '@loaders/PageLoader';
import {
  selectAppConfigHydrated,
  selectHasApprovedBusiness,
  selectNeedsBusinessSearch,
} from '@redux/merchant/merchantSlice';
import { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router';

const SuspenseWrapper = ({ children, fallback = <PageLoader /> }) => (
  <Suspense fallback={fallback}>{children}</Suspense>
);

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, needsOnboarding, mustChangePassword } = useSelector(
    (state) => state.auth,
  );
  const { profileCompleted } = useSelector((state) => state.onboarding);
  const needsBusinessSearch = useSelector(selectNeedsBusinessSearch);
  const appConfigHydrated = useSelector(selectAppConfigHydrated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} state={{ from: location }} replace />;
  }

  if (mustChangePassword) {
    return <Navigate to={ROUTE_PATHS.RESET_PASSWORD} replace />;
  }

  if (needsOnboarding) {
    // New merchants (no business yet) may use Launch Pad, Resources, and Profile.
    const onAllowedShellPath =
      location.pathname === ROUTE_PATHS.SETUP_GUIDE ||
      location.pathname === ROUTE_PATHS.RESOURCES ||
      location.pathname === ROUTE_PATHS.PROFILE;
    const canAccessAppShell =
      profileCompleted && appConfigHydrated && (!needsBusinessSearch || onAllowedShellPath);

    if (!canAccessAppShell) {
      return <Navigate to={getOnboardingEntryPath(profileCompleted)} replace />;
    }
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { isAuthenticated, needsOnboarding, mustChangePassword } = useSelector(
    (state) => state.auth,
  );
  const { profileCompleted } = useSelector((state) => state.onboarding);
  const location = useLocation();

  if (isAuthenticated && mustChangePassword) {
    if (location.pathname !== ROUTE_PATHS.RESET_PASSWORD) {
      return <Navigate to={ROUTE_PATHS.RESET_PASSWORD} replace />;
    }
    return children;
  }

  if (isAuthenticated && needsOnboarding) {
    if (!ONBOARDING_ROUTES.includes(location.pathname)) {
      return <Navigate to={getOnboardingEntryPath(profileCompleted)} replace />;
    }
    return children;
  }

  if (isAuthenticated) {
    return <Navigate to={getDefaultHomePath()} replace />;
  }

  return children;
};

export const ResetPasswordRoute = ({ children }) => {
  const { isAuthenticated, mustChangePassword } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  if (!mustChangePassword) {
    return <Navigate to={getDefaultHomePath()} replace />;
  }

  return children;
};

/** Keeps a must-change-password session on /reset-password for every other URL. */
export const PasswordChangeLock = ({ children, enabled = true }) => {
  const { isAuthenticated, mustChangePassword } = useSelector((state) => state.auth);
  const { pathname } = useLocation();

  if (enabled && isAuthenticated && mustChangePassword && pathname !== ROUTE_PATHS.RESET_PASSWORD) {
    return <Navigate to={ROUTE_PATHS.RESET_PASSWORD} replace />;
  }

  return children;
};

export const OnboardingRoute = ({ children }) => {
  const { isAuthenticated, needsOnboarding, pendingPhone } = useSelector((state) => state.auth);
  const { profileCompleted } = useSelector((state) => state.onboarding);
  const hasApprovedBusiness = useSelector(selectHasApprovedBusiness);
  const needsBusinessSearch = useSelector(selectNeedsBusinessSearch);
  const appConfigHydrated = useSelector(selectAppConfigHydrated);
  const location = useLocation();

  if (location.pathname === ROUTE_PATHS.VERIFY_OTP) {
    // After OTP success the user is authenticated — block browser back to this screen
    if (isAuthenticated) {
      if (needsOnboarding) {
        return <Navigate to={getOnboardingEntryPath(profileCompleted)} replace />;
      }
      return <Navigate to={getDefaultHomePath()} replace />;
    }
    if (!pendingPhone) {
      return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }
    return children;
  }

  if (location.pathname === ROUTE_PATHS.CREATE_PROFILE) {
    if (!isAuthenticated) {
      return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }
    if (profileCompleted) {
      return <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace />;
    }
    return children;
  }

  if (location.pathname === ROUTE_PATHS.SEARCH_BUSINESS) {
    if (!isAuthenticated) {
      return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }
    if (appConfigHydrated && !needsBusinessSearch && !isAddNewBusinessNavigation(location.state)) {
      return <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace />;
    }
    if (!profileCompleted) {
      return <Navigate to={ROUTE_PATHS.CREATE_PROFILE} replace />;
    }
    return children;
  }

  if (location.pathname === ROUTE_PATHS.BUSINESS_DETAIL) {
    if (!isAuthenticated) {
      return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }
    if (appConfigHydrated && !needsBusinessSearch && !isAddNewBusinessNavigation(location.state)) {
      return <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace />;
    }
    if (!profileCompleted) {
      return <Navigate to={ROUTE_PATHS.CREATE_PROFILE} replace />;
    }
    return children;
  }

  if (location.pathname === ROUTE_PATHS.REQUEST_SUBMITTED) {
    if (!isAuthenticated) {
      return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }
    if (hasApprovedBusiness) {
      return <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace />;
    }
    if (!profileCompleted) {
      return <Navigate to={ROUTE_PATHS.CREATE_PROFILE} replace />;
    }
    return children;
  }

  return children;
};

export const PermissionRoute = ({ children, permission, permissions, role, fallback = null }) => {
  const { can, canAll, hasRole } = usePermission();
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  if (role && !hasRole(role)) {
    return fallback || <Navigate to={ROUTE_PATHS.UNAUTHORIZED} replace />;
  }

  if (permissions?.length && !canAll(permissions)) {
    return fallback || <Navigate to={ROUTE_PATHS.UNAUTHORIZED} replace />;
  }

  if (permission && !can(permission)) {
    return fallback || <Navigate to={ROUTE_PATHS.UNAUTHORIZED} replace />;
  }

  return children;
};

export const LazyRoute = ({ children, fallback }) => (
  <SuspenseWrapper fallback={fallback}>{children}</SuspenseWrapper>
);

export default ProtectedRoute;
